import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AppMenuitem } from './app.menuitem';
import { AuthService } from '../../core/services/auth-service';
import { BrandingService } from '@/services/branding.service';

// هر آیتم فقط برای کسی که یکی از دسترسی‌های آن را دارد نمایش داده می‌شود ('admin' = فقط مدیر، بدون دسترسی = همه)
interface GuardedItem extends MenuItem {
  permissions?: string[];
}

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [CommonModule, AppMenuitem, RouterModule],
  template: `<ul class="layout-menu">
    <ng-container *ngFor="let item of model; let i = index">
      <li app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="true"></li>
      <li *ngIf="item.separator" class="menu-separator"></li>
    </ng-container>
  </ul> `,
})
export class AppMenu implements OnInit {
  model: MenuItem[] = [];

  constructor(
    private auth: AuthService,
    private branding: BrandingService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.build('');
    // عنوان اولین بخش منو همان نام سامانه است
    this.branding.load().subscribe((s) => this.build(s.branding?.appName ?? ''));
  }

  private build(appName: string) {
    const sections: { label: string; items: GuardedItem[] }[] = [
      {
        label: appName || 'داشبورد',
        items: [{ label: 'داشبورد', icon: 'pi pi-fw pi-home', routerLink: ['/'], permissions: ['dashboard.view'] }],
      },
      {
        label: 'رزرو و مشتریان',
        items: [
          { label: 'ثبت رزرو جدید', icon: 'pi pi-fw pi-calendar-plus', routerLink: ['/reservations/new'], permissions: ['reservations.create'] },
          { label: 'وضعیت اتاق‌ها', icon: 'pi pi-fw pi-calendar', routerLink: ['/room-availability'], permissions: ['reservations.view'] },
          { label: 'لیست رزروها', icon: 'pi pi-fw pi-list', routerLink: ['/reservations'], permissions: ['reservations.view'] },
          { label: 'درخواست‌های رزرو', icon: 'pi pi-fw pi-check-square', routerLink: ['/booking-requests'], permissions: ['reservations.view'] },
          { label: 'انتظار مشتری', icon: 'pi pi-fw pi-hourglass', routerLink: ['/waitlist'], permissions: ['queue.manage'] },
          { label: 'مشتریان و کیف پول', icon: 'pi pi-fw pi-id-card', routerLink: ['/customers'], permissions: ['customers.view'] },
          { label: 'صدور فاکتور الکترونیکی', icon: 'pi pi-fw pi-file', routerLink: ['/payment-links'], permissions: ['payments.manage', 'payments.links'] },
        ],
      },
      {
        label: 'محل‌ها و زمان‌بندی',
        items: [
          { label: 'مدیریت محل‌های قابل رزرو', icon: 'pi pi-fw pi-home', routerLink: ['/rooms'], permissions: ['rooms.manage'] },
          { label: 'مدیریت امکانات', icon: 'pi pi-fw pi-list', routerLink: ['/amenities'], permissions: ['rooms.manage'] },
          { label: 'بازه‌های زمانی', icon: 'pi pi-fw pi-clock', routerLink: ['/time-slots'], permissions: ['rooms.manage'] },
          { label: 'قیمت‌گذاری', icon: 'pi pi-fw pi-tags', routerLink: ['/pricing'], permissions: ['pricing.manage'] },
          { label: 'غیرفعال‌سازی رزرو', icon: 'pi pi-fw pi-ban', routerLink: ['/booking-blocks'], permissions: ['blocks.manage'] },
        ],
      },
      {
        label: 'رویداد، پرسنل و نظرسنجی',
        items: [
          { label: 'رویدادها', icon: 'pi pi-fw pi-star', routerLink: ['/events'], permissions: ['events.manage'] },
          { label: 'مدیریت شیفت', icon: 'pi pi-fw pi-id-card', routerLink: ['/shifts'], permissions: ['shifts.manage'] },
          { label: 'نظرسنجی', icon: 'pi pi-fw pi-comments', routerLink: ['/surveys'], permissions: ['surveys.view'] },
        ],
      },
      {
        label: 'مدیریت سامانه',
        items: [
          { label: 'کاربران و دسترسی‌ها', icon: 'pi pi-fw pi-users', routerLink: ['/users'], permissions: ['admin'] },
          { label: 'مدیریت پیامک', icon: 'pi pi-fw pi-envelope', routerLink: ['/sms'], permissions: ['sms.view', 'sms.settings'] },
          { label: 'مدیریت پرداخت', icon: 'pi pi-fw pi-credit-card', routerLink: ['/payments-management'], permissions: ['payments.report', 'payments.settings'] },
          { label: 'چاپگرها', icon: 'pi pi-fw pi-print', routerLink: ['/printers'], permissions: ['printers.manage', 'printers.print'] },
          { label: 'ایستگاه چاپ', icon: 'pi pi-fw pi-desktop', routerLink: ['/print-station'], permissions: ['printers.print'] },
          { label: 'ظاهر و رنگ‌بندی', icon: 'pi pi-fw pi-palette', routerLink: ['/appearance'], permissions: ['appearance.manage'] },
          { label: 'تنظیمات رزرو', icon: 'pi pi-fw pi-sliders-h', routerLink: ['/settings'], permissions: ['settings.manage'] },
          { label: 'هشدارها', icon: 'pi pi-fw pi-bell', routerLink: ['/alerts'] },
          { label: 'لاگ سامانه', icon: 'pi pi-fw pi-history', routerLink: ['/logs'], permissions: ['logs.view'] },
          { label: 'راهنمای سامانه', icon: 'pi pi-fw pi-question-circle', routerLink: ['/help'] },
        ],
      },
      {
        // در موبایل منوی کاربر بالای صفحه جای کمی دارد؛ پروفایل و خروج در منو هم هست
        label: 'حساب کاربری',
        items: [
          { label: 'پروفایل', icon: 'pi pi-fw pi-user', routerLink: ['/profile'] },
          {
            label: 'خروج از سامانه',
            icon: 'pi pi-fw pi-sign-out',
            command: () => {
              this.auth.logout();
              this.router.navigate(['/auth/login'], { replaceUrl: true });
            },
          },
        ],
      },
    ];

    const allowed = (item: GuardedItem) =>
      item.permissions?.includes('admin') ? this.auth.isAdmin() : this.auth.can(...(item.permissions ?? []));

    this.model = sections
      .map((section) => ({
        label: section.label,
        items: section.items.filter(allowed).map(({ permissions, ...item }) => item),
      }))
      .filter((section) => section.items.length);
  }
}
